import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Layers,
  Boxes,
  Tag,
  Truck,
  Shirt,
  PackagePlus,
  Shield,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Vos fournitures de déménagement livrées à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Notre service de transport prend en charge
              l&apos;acheminement de tout votre matériel directement sur
              le pas de votre porte ou dans votre garage {city.aCity},
              mais aussi à Verneuil-sur-Seine, Triel-sur-Seine, Chapet,
              Médan et Villennes-sur-Seine.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous fixons ensemble un rendez-vous précis et
              rigoureusement respecté pour ne pas bloquer votre journée.
              Toutes vos fournitures vous sont remises à plat, scellées
              sous un film de protection étanche pour vous garantir des
              cartons propres, secs et immédiatement prêts à
              l&apos;emploi.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Couverture locale élargie",
                body: "Livraison à Vernouillet et tout autour : Verneuil-sur-Seine, Triel-sur-Seine, Chapet, Médan, Villennes-sur-Seine.",
                Icon: Truck,
              },
              {
                title: "Créneau horaire respecté",
                body: "Rendez-vous précis fixé ensemble et rigoureusement tenu : pas de journée bloquée à attendre votre livreur.",
                Icon: ShieldCheck,
              },
              {
                title: "Conditionnement irréprochable",
                body: "Cartons à plat sous film étanche, propres, secs et prêts à monter pour conserver 100 % de leur rigidité.",
                Icon: Layers,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons pour vêtements ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Vos garde-robes mobiles
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons pour vêtements : vos penderies mobiles à portée de
            main
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le transport de la garde-robe est souvent une corvée
            redoutée lors des préparatifs. Pour vous éviter de froisser
            vos plus belles pièces, nos solutions spécifiques font la
            différence.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Le carton penderie vertical",
              body: "Équipé d'une tringle métallique solide, ce carton sur-mesure vous permet de transférer vos manteaux, robes, costumes et vestes directement sur leurs cintres, sans les décrocher de votre armoire.",
              Icon: Shirt,
            },
            {
              title: "Gain de temps et zéro repassage",
              body: `Vos vêtements restent suspendus et protégés de la poussière pendant tout le trajet. Une fois arrivé dans votre nouveau logement ${city.aCity}, il vous suffit de les replacer instantanément dans votre dressing, impeccables et sans un pli.`,
              Icon: Sparkles,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-vernouillet-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons penderies
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-vernouillet-cta-kits"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Découvrir nos kits complets
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Emballages de protection ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Bouclier antichoc
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Emballages de protection : le bouclier antichoc
            indispensable
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour que vos objets fragiles, vos appareils électroniques et
            vos meubles traversent les manipulations sans la moindre
            égratignure, notre gamme d&apos;accessoires professionnels
            assure un capitonnage total.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Film à bulles */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <PackagePlus className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Film à bulles haute densité
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Doté de bulles robustes qui résistent à la pression, il
              enveloppe vos cadres, miroirs, bibelots et écrans pour
              amortir les vibrations et les chocs inhérents au transport
              routier.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              Sa souplesse permet d&apos;épouser toutes les formes —
              y compris les angles vifs des écrans plats ou les pieds
              sculptés d&apos;un guéridon ancien — pour un calage
              parfait à l&apos;intérieur du carton. Idéal pour
              transporter votre matériel high-tech et vos objets de
              décoration en toute confiance.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Bulles d&apos;air haute densité
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Idéal cadres, miroirs, écrans
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Amortit vibrations et chocs
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                S&apos;adapte à toutes les formes
              </div>
            </div>
          </div>

          {/* Housses de protection */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shield className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Housses de protection épaisses
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Spécialement taillées pour vos matelas, canapés et gros
              électroménagers, ces housses imperméables protègent vos
              pièces volumineuses des salissures et des frottements dans
              les couloirs ou le camion.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              Conçues dans un polyéthylène épais, elles forment une
              barrière anti-poussière et anti-humidité indispensable si
              votre déménagement {city.aCity} doit se dérouler par
              temps de pluie ou si vos meubles transitent par un
              garde-meubles temporaire.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Housses matelas (1 et 2 places)
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Protection canapés &amp; fauteuils
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Polyéthylène épais imperméable
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Anti-poussière et anti-humidité
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-vernouillet-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Voir les protections
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-vernouillet-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-vernouillet-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
